/**
 * Helper to pad single digits with a leading zero.
 */
const pad = (val) => val.toString().padStart(2, "0");

/**
 * Converts a total number of seconds into an HH:mm:ss string format.
 *
 * @param {number} totalSeconds - The duration in seconds.
 * @returns {string} The formatted time string (HH:mm:ss).
 */
function formatSecondsToTime(totalSeconds) {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
}

/**
 * Calculates the time elapsed and remaining time against a 4-hour session.
 * Crucially, it treats the input time as UTC to ensure correct duration calculation.
 *
 * @param {string} startTimeString - The start time in "YYYY-MM-DD HH:mm:ss" format (assumed UTC).
 * @returns {object} Calculated time metrics.
 */
export default function calculateProgress(startTimeString) {
  const TOTAL_SESSION_HOURS = 4;
  const SECONDS_PER_HOUR = 3600;
  const TOTAL_SESSION_SECONDS = TOTAL_SESSION_HOURS * SECONDS_PER_HOUR; // 14400 seconds

  // 1. Parse Start Time as UTC
  const startIsoString = startTimeString.replace(" ", "T") + "Z";
  const startTimeMs = new Date(startIsoString).getTime();

  if (isNaN(startTimeMs)) {
    console.error("Error: Invalid startTimeString format.");
    return {
      elapsedSeconds: 0,
      elapsedTimeFormatted: "00:00:00",
      remainingSeconds: TOTAL_SESSION_SECONDS,
      remainingTimeFormatted: formatSecondsToTime(TOTAL_SESSION_SECONDS),
      progressPercent: "0.00",
    };
  }

  // 2. Calculate Elapsed Time
  const nowTimeMs = new Date().getTime();
  const elapsedMs = nowTimeMs - startTimeMs;
  const elapsedSeconds = Math.max(0, Math.floor(elapsedMs / 1000));

  // 3. Calculate Remaining Time
  const remainingSeconds = Math.max(0, TOTAL_SESSION_SECONDS - elapsedSeconds);

  // 4. Calculate Progress Percentage
  let progressPercent = 0;
  if (TOTAL_SESSION_SECONDS > 0) {
    progressPercent = (elapsedSeconds / TOTAL_SESSION_SECONDS) * 100;
    progressPercent = Math.min(100, Math.max(0, progressPercent));
  }

  // 5. Format Times
  const elapsedTimeFormatted = formatSecondsToTime(elapsedSeconds);
  const remainingTimeFormatted = formatSecondsToTime(remainingSeconds);

  // 6. Return Results
  return {
    elapsedSeconds: elapsedSeconds,
    elapsedTimeFormatted: elapsedTimeFormatted,
    remainingSeconds: remainingSeconds,
    remainingTimeFormatted: remainingTimeFormatted,
    progressPercent: progressPercent.toFixed(2),
    // Optional: for styling the progress bar
    progressClass:
      progressPercent < 50 ? "low" : progressPercent < 80 ? "mid" : "high",
  };
}
